import { useState } from "react";
import { ITEM_CONDITIONS, POP_WEIGHTS, ITEM_HAZMAT_VALUES, flattenInventoryCategories, inventoryCategoryPair, whatnotConditions } from "@farnsworth/shared";
import { Button, ErrorBanner, Field, Input, Modal, Select } from "./ui.js";
import { useBulkUpdate } from "../hooks/useInventory.js";
import type { Category } from "../types.js";

/** Tri-state so a field can be left alone, set true, or set false. */
type Tri = "" | "true" | "false";

export function MassEditDialog({
  ids,
  categories,
  onClose,
}: {
  ids: string[];
  categories: Category[];
  onClose: () => void;
}) {
  const bulkUpdate = useBulkUpdate();

  const [categoryId, setCategoryId] = useState("");
  const [condition, setCondition] = useState("");
  const [hazmat, setHazmat] = useState("");
  const flatCategories = flattenInventoryCategories(categories);
  const pair = inventoryCategoryPair(categories, categoryId);
  const conditions = categoryId ? whatnotConditions(pair.category, pair.subCategory) : ITEM_CONDITIONS;
  const [location, setLocation] = useState("");
  const [owner, setOwner] = useState("");
  const [series, setSeries] = useState("");
  const [exclusiveTo, setExclusiveTo] = useState("");
  const [popWeight, setPopWeight] = useState("");
  const [quantity, setQuantity] = useState("");
  const [isWebsiteFeatured, setIsWebsiteFeatured] = useState<Tri>("");
  const [sold, setSold] = useState<Tri>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  function buildPatch(): Record<string, unknown> {
    const patch: Record<string, unknown> = {};
    if (categoryId) patch.categoryId = categoryId;
    if (condition) patch.condition = condition;
    if (hazmat) patch.hazmat = hazmat;
    if (location.trim()) patch.location = location.trim();
    if (owner.trim()) patch.owner = owner.trim();
    if (series.trim()) patch.series = series.trim();
    if (exclusiveTo.trim()) patch.exclusiveTo = exclusiveTo.trim();
    if (popWeight) patch.popWeight = popWeight;
    if (quantity.trim()) patch.quantity = Number.parseInt(quantity, 10);
    if (isWebsiteFeatured) patch.isWebsiteFeatured = isWebsiteFeatured === "true";
    if (sold) patch.sold = sold === "true";
    return patch;
  }

  const changedCount = Object.keys(buildPatch()).length;

  async function submit() {
    setFormError(null);
    const patch = buildPatch();

    if (Object.keys(patch).length === 0) {
      setFormError("Change at least one field. Blank fields are left untouched.");
      return;
    }

    try {
      const summary = await bulkUpdate.mutateAsync({ ids, patch });
      setResult(
        `${summary.updated} item(s) updated` +
          (summary.unchanged > 0 ? `, ${summary.unchanged} already matched` : "") +
          (summary.notFound > 0 ? `, ${summary.notFound} not found` : "") +
          ".",
      );
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Mass edit failed.");
    }
  }

  return (
    <Modal title={`Mass edit ${ids.length} item(s)`} onClose={onClose} wide>
      <div className="space-y-4">
        {formError ? <ErrorBanner message={formError} /> : null}
        {result ? (
          <div className="rounded-md border border-emerald-800 bg-emerald-950/60 px-3 py-2 text-sm text-emerald-200">
            {result}
          </div>
        ) : null}

        <p className="text-sm text-slate-400">
          Leave a field blank to leave it unchanged on every selected item.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Category">
            <Select value={categoryId} onChange={(event) => { setCategoryId(event.target.value); setCondition(""); }}>
              <option value="">Leave unchanged</option>
              {flatCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.parentId ? `${flatCategories.find((entry) => entry.id === category.parentId)?.name} → ${category.name}` : category.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Condition">
            <Select value={condition} onChange={(event) => setCondition(event.target.value)}>
              <option value="">Leave unchanged</option>
              {conditions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Hazmat">
            <Select value={hazmat} onChange={(event) => setHazmat(event.target.value)}>
              <option value="">Leave unchanged</option>
              {ITEM_HAZMAT_VALUES.map((value) => <option key={value}>{value}</option>)}
            </Select>
          </Field>
          <Field label="Location">
            <Input
              value={location}
              onChange={(event) => setLocation(event.target.value)}
              placeholder="Leave unchanged"
            />
          </Field>
          <Field label="Owner">
            <Input value={owner} maxLength={200} onChange={(event) => setOwner(event.target.value)} placeholder="e.g. Personal or consignment" />
          </Field>
          <Field label="Series">
            <Input
              value={series}
              onChange={(event) => setSeries(event.target.value)}
              placeholder="Leave unchanged"
            />
          </Field>
          <Field label="Exclusive to">
            <Input
              value={exclusiveTo}
              onChange={(event) => setExclusiveTo(event.target.value)}
              placeholder="Leave unchanged"
            />
          </Field>
          <Field label="Shipping weight">
            <Select value={popWeight} onChange={(event) => setPopWeight(event.target.value)}>
              <option value="">Leave unchanged</option>
              {POP_WEIGHTS.map((weight) => (
                <option key={weight} value={weight}>
                  {weight}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Quantity">
            <Input
              type="number"
              min={0}
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder="Leave unchanged"
            />
          </Field>
          <Field label="Feature on website">
            <Select
              value={isWebsiteFeatured}
              onChange={(event) => setIsWebsiteFeatured(event.target.value as Tri)}
            >
              <option value="">Leave unchanged</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </Field>
          <Field label="Sold">
            <Select value={sold} onChange={(event) => setSold(event.target.value as Tri)}>
              <option value="">Leave unchanged</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </Field>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={submit} disabled={bulkUpdate.isPending || changedCount === 0}>
            {bulkUpdate.isPending
              ? "Applying…"
              : `Apply ${changedCount} change${changedCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
